import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface Comentario {
  id: string;
  content: string;
  user_id: string;
  org_id: string;
  embarque_id: string | null;
  documento_id: string | null;
  mentions: any | null;
  created_at: string;
  // Joined
  user_email?: string;
}

interface UseComentariosOptions {
  entityId: string | null;
  entityType: "embarque" | "documento";
}

export function useComentarios({ entityId, entityType }: UseComentariosOptions) {
  const { orgId, user } = useAuth();
  const queryClient = useQueryClient();
  const queryKey = ["comentarios", entityType, entityId];

  const filterCol = entityType === "embarque" ? "embarque_id" : "documento_id";

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!entityId || !orgId) return [];
      const { data, error } = await supabase
        .from("comentarios")
        .select("*")
        .eq("org_id", orgId)
        .eq(filterCol, entityId)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as Comentario[];
    },
    enabled: !!entityId && !!orgId,
  });

  // Realtime subscription
  useEffect(() => {
    if (!entityId || !orgId) return;

    const channel = supabase
      .channel(`comentarios-${entityType}-${entityId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "comentarios",
          filter: `${filterCol}=eq.${entityId}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [entityId, entityType, orgId, filterCol, queryClient, queryKey]);

  const enviarComentario = useMutation({
    mutationFn: async (payload: string | { content: string; mentions: any[] | null }) => {
      if (!entityId || !orgId || !user) throw new Error("Missing context");
      const content = typeof payload === "string" ? payload : payload.content;
      const mentions = typeof payload === "string" ? null : payload.mentions;
      const row = {
        content: content.trim(),
        org_id: orgId,
        user_id: user.id,
        mentions,
        [filterCol]: entityId,
      };
      const { error } = await supabase.from("comentarios").insert([row as any]);
      if (error) throw error;

      // Dispatch notifications for each mentioned user
      if (mentions && mentions.length > 0) {
        const notifRows = mentions
          .filter((m: any) => m.user_id !== user.id) // Don't notify yourself
          .map((m: any) => ({
            user_id: m.user_id,
            actor_id: user.id,
            org_id: orgId,
            tipo: "mencion",
            titulo: "Te han mencionado en un comentario",
            mensaje: content.trim().slice(0, 80),
            metadata: { entityId, entityType, type: "comment" },
          }));
        if (notifRows.length > 0) {
          await supabase.from("notificaciones").insert(notifRows);
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    comentarios: query.data ?? [],
    isLoading: query.isLoading,
    enviarComentario,
  };
}
